// Browse mega-menu on catalogue-LINKING screens (My Account, Admin Users, PDP).
//
// On the /products catalogue page the Browse button opens BrowseNav as a header
// mega-menu and selecting a node drills the on-page listing (Products.tsx). Every
// OTHER screen used to route Browse straight to /products (useHeaderChrome's
// `onBrowseClick: goToCatalogue`), so the menu only ever appeared on the catalogue
// page. This hook makes Browse open the SAME mega-menu in place everywhere: it
// owns the open-state + drill and returns the SiteHeader disclosure props plus the
// overlay to drop into the header's `children` slot.
//
// Off-catalogue there is no listing to drill, so selecting a node hands it to
// `/products?node=<id>` — Story 7.35 (ADO-2032): a shareable canonical URL the
// /products mount restores (deep-link node → listing + drill), replacing the former
// transient `location.state` handover. The favourites view uses the same path.

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import type { CategoryNode } from '../../api/browse.client';
import { BrowseMenu } from './BrowseMenu';
import { useBrowseDrill } from './useBrowseDrill';
import { productsPath } from '../Products/productsUrlState';

// DOM id of the browse mega-menu overlay — shared between the SiteHeader Browse
// trigger's `aria-controls` and the overlay container it points at. One header
// renders at a time, so a single literal is safe across screens. Exported so the
// /products header (Products.tsx) reuses the SAME id instead of a second copy.
export const BROWSE_MENU_ID = 'browse-mega-menu';

export interface BrowseMegaMenu {
  /** Toggles the mega-menu — spread onto <SiteHeader onBrowseClick={...} />. */
  readonly onBrowseClick: () => void;
  /** Disclosure state — spread onto <SiteHeader browseExpanded={...} />. */
  readonly browseExpanded: boolean;
  /** Overlay container id — spread onto <SiteHeader browseMenuId={...} />. */
  readonly browseMenuId: string;
  /** Closes the mega-menu on an outside click / hover-out — spread onto
   *  <SiteHeader onBrowseDismiss={...} />. */
  readonly onBrowseDismiss: () => void;
  /** The overlay to render as the SiteHeader's `children` (null while closed). */
  readonly browseOverlay: React.ReactNode;
}

export function useBrowseMegaMenu(): BrowseMegaMenu {
  const navigate = useNavigate();
  const drill = useBrowseDrill();
  const [browseOpen, setBrowseOpen] = useState(false);

  // Escape closes the mega-menu (parity with Products.tsx). preventDefault claims
  // the key so a window-level layer (the upsell toast) does not also dismiss on it.
  useEffect(() => {
    if (!browseOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setBrowseOpen(false);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [browseOpen]);

  const selectNode = (node: CategoryNode) => {
    // No listing to drill here — hand the node to `/products?node=<id>`, a
    // shareable URL the catalogue restores on mount (deep-link node → listing).
    setBrowseOpen(false);
    navigate(productsPath({ node: node.id }));
  };

  const browseOverlay = browseOpen ? (
    <BrowseMenu
      id={BROWSE_MENU_ID}
      activeDepartmentId={drill.activeDepartment?.id ?? null}
      activeCategoryId={drill.activeCategory?.id ?? null}
      selectedNodeId={drill.selectedNode?.id ?? null}
      onSelectNode={selectNode}
      onExpandNode={drill.expandNode}
      onClose={() => setBrowseOpen(false)}
    />
  ) : null;

  return {
    onBrowseClick: () => setBrowseOpen((o) => !o),
    browseExpanded: browseOpen,
    browseMenuId: BROWSE_MENU_ID,
    onBrowseDismiss: () => setBrowseOpen(false),
    browseOverlay,
  };
}
